import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Check, ArrowLeft, Hammer, Rocket, RefreshCcw } from "lucide-react";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { trackPageview } from "@/lib/analytics";

const fmtEur = (cents) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const fireCelebration = () => {
  const colors = ["#d4af37", "#0a0a0a", "#ffffff", "#f1e2a5"];
  confetti({ particleCount: 90, spread: 75, origin: { y: 0.25 }, colors });
  setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 60, origin: { x: 0, y: 0.4 }, colors }), 250);
  setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 60, origin: { x: 1, y: 0.4 }, colors }), 450);
  setTimeout(() => confetti({ particleCount: 120, spread: 120, origin: { y: 0.3 }, colors, scalar: 0.8 }), 700);
};

const STEPS = [
  { icon: Check, text: "Zahlung eingegangen — deine Bestellung ist gesichert.", pendingText: "Zahlung abschließen — sobald sie bestätigt ist, geht es los." },
  { icon: Hammer, text: "Ich melde mich persönlich bei dir und wir besprechen Inhalte & Design." },
  { icon: Rocket, text: "Ich baue deine Website und zeige sie dir zur Abnahme." },
  { icon: RefreshCcw, text: "Ab dem Tag der Fertigstellung läuft der monatliche Betrieb automatisch." },
];

export default function WebsiteOrderPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(undefined);

  const load = () =>
    api.get(`/website-orders/${id}/public`).then(({ data }) => setOrder(data)).catch(() => setOrder(null));

  useEffect(() => {
    document.title = "Bestellung | JUSTIN HILFT";
    trackPageview("/website-bestellung");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Polling, bis der Webhook die Zahlung bestätigt hat
  useEffect(() => {
    if (params.get("bezahlt") !== "1" || !order || order.status !== "checkout") return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      await load();
      if (tries >= 15) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, order?.status]);

  const paid = order && order.status !== "checkout";
  const justPaid = params.get("bezahlt") === "1";
  useEffect(() => {
    if (justPaid && paid) fireCelebration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justPaid, paid]);

  if (order === undefined) {
    return (
      <main className="min-h-screen bg-paper text-ink flex items-center justify-center" data-testid="order-loading">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </main>
    );
  }

  if (order === null) {
    return (
      <main className="min-h-screen bg-paper text-ink flex items-center justify-center px-6" data-testid="order-not-found">
        <div className="text-center">
          <p className="font-display uppercase tracking-tight text-2xl mb-4">Bestellung nicht gefunden</p>
          <Link to="/" className="text-sm text-zinc-500 hover:text-ink">Zur Startseite</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="website-order-page" className="min-h-screen bg-paper text-ink px-6 lg:px-12 py-12">
      <div className="max-w-2xl mx-auto">
        <p className="font-display font-bold uppercase tracking-tight text-lg mb-1">
          Justin Hilft<span className="text-gold">.</span>
        </p>
        <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-10">Website-Bestellung</p>

        {justPaid && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="border border-gold/40 bg-gold/5 p-8 lg:p-10 mb-8 text-center"
            data-testid="order-danke-state"
          >
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.2 }}
              className="mx-auto mb-6 w-16 h-16 rounded-full bg-gold flex items-center justify-center"
            >
              <Check size={30} className="text-ink" />
            </motion.span>
            {paid ? (
              <>
                <p className="font-display uppercase tracking-tight text-2xl lg:text-3xl mb-3">Danke für deinen Kauf!</p>
                <p className="text-sm lg:text-base text-zinc-600 leading-relaxed max-w-md mx-auto">
                  Deine Zahlung über <strong className="text-ink">{fmtEur(order.package_price_cents)}</strong> ist
                  eingegangen. Eine Bestätigung ist per E-Mail unterwegs zu dir.
                </p>
              </>
            ) : (
              <>
                <p className="font-display uppercase tracking-tight text-2xl lg:text-3xl mb-3 flex items-center justify-center gap-3">
                  <Loader2 size={22} className="animate-spin text-gold" /> Zahlung wird bestätigt
                </p>
                <p className="text-sm lg:text-base text-zinc-600 leading-relaxed max-w-md mx-auto">
                  Deine Zahlung wird gerade geprüft — die Seite aktualisiert sich automatisch.
                </p>
              </>
            )}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="border border-black/10 bg-white p-8 lg:p-10"
        >
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Bestellnummer</p>
              <p className="font-display uppercase tracking-tight text-2xl" data-testid="order-number">{order.number}</p>
            </div>
            <span
              className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] ${
                paid ? "border-emerald-600/40 text-emerald-700 bg-emerald-600/5" : "border-gold/50 text-gold bg-gold/5"
              }`}
              data-testid="order-status"
            >
              {paid ? "Bezahlt — in Arbeit" : "Zahlung offen"}
            </span>
          </div>

          <dl className="space-y-5 text-sm mb-10">
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Paket</dt>
              <dd className="text-base">Website {order.package_label}</dd>
              {order.express && (
                <dd className="mt-1 inline-flex items-center gap-1.5 border border-gold/50 bg-gold/5 text-gold px-2.5 py-1 text-[10px] uppercase tracking-[0.2em]" data-testid="order-express-badge">
                  Express — fertig in 48 Stunden
                </dd>
              )}
              <dd className="text-zinc-600 mt-1">{order.support_label}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Preis</dt>
              <dd className="font-display text-3xl" data-testid="order-price">{fmtEur(order.total_price_cents)}</dd>
              <dd className="text-xs text-zinc-500 mt-1">
                einmalig{order.express ? " (inkl. Express-Lieferung)" : ""} — danach {fmtEur(order.monthly_cents)} / Monat Betrieb ab Fertigstellung. Gemäß § 19 UStG
                keine Umsatzsteuer.
              </dd>
            </div>
          </dl>

          <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-5">So geht es weiter</p>
          <ol className="space-y-4">
            {STEPS.map((s, i) => (
              <li key={i} className="flex items-start gap-4" data-testid={`order-step-${i}`}>
                <span className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${i === 0 && paid ? "bg-gold text-ink" : "bg-black/5 text-zinc-500"}`}>
                  <s.icon size={14} />
                </span>
                <p className="text-sm text-zinc-600 leading-relaxed pt-1.5">{i === 0 && !paid && s.pendingText ? s.pendingText : s.text}</p>
              </li>
            ))}
          </ol>
        </motion.div>

        <Link to="/" data-testid="order-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-ink transition-colors duration-300 mt-8">
          <ArrowLeft size={14} /> Zurück zur Startseite
        </Link>
      </div>
    </main>
  );
}
